import { MouseEvent, ReactNode, useEffect, useState } from "react";
import { Button, Modal, Skeleton, message } from "antd";
import DataKitApi from "../../../../api/DataKitApi";
import { ItemMenuDto, MenuDetailDto, MenuPriceRowDto } from "../../../../domain/DataKitDto";
import { dataKitThumbColors } from "./DataKitThumb";
import { useMenuLocation } from "./menuLocation";
import "./DataKitMenuView.scss";

/*
 * Menus come from designs (Oct 12): a menu is a design that uses library items, named after the
 * design. Nothing here creates, renames or attaches one - this is the read-only view a Menus row
 * opens, and the "Used on N menus" list that leads to it.
 */

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "Large", "Blackened · Style" - which price on the item a menu price row is. */
function priceRowLabel(row: MenuPriceRowDto): string {
  if (row.modifierId) {
    return [row.modifierName, row.modifierGroupName].filter(Boolean).join(" · ") || "Modifier";
  }
  return (row.size || "").trim();
}

function LibraryChip({ name }: { name: string }) {
  const { bg, fg } = dataKitThumbColors(name || "Untitled");
  return (
    <span className="menu-view-chip" style={{ background: bg, color: fg }}>
      {name || "Untitled"}
    </span>
  );
}

interface MenuViewProps {
  designId: string;
  onClose: () => void;
}

/** A menu's read-only view: the libraries it uses items from, and its own menu prices. */
export function DataKitMenuView({ designId, onClose }: MenuViewProps) {
  const [menu, setMenu] = useState<MenuDetailDto | null>(null);
  const locationOf = useMenuLocation();

  useEffect(() => {
    let cancelled = false;
    setMenu(null);
    DataKitApi.getMenu(designId)
      .then((res) => {
        if (!cancelled) setMenu(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        message.error("Could not load this menu");
        onClose();
      });
    return () => {
      cancelled = true;
    };
    // onClose is only called on failure; a new one each render must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId]);

  return (
    <Modal className={"data-kit-menu-view clean one"} open width={560} onCancel={onClose}>
      {!menu ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <>
          <header>
            <h2>{menu.name}</h2>
          </header>
          {locationOf(menu) && <p className="menu-view-sub">{locationOf(menu)}</p>}

          <div className="menu-view-section">
            <span className="menu-view-label">Uses items from</span>
            {menu.libraries.map((lib) => (
              <div className="menu-view-row" key={lib.id}>
                <LibraryChip name={lib.name} />
              </div>
            ))}
            <p className="menu-view-note">{plural(menu.itemCount, "library item")} on this menu.</p>
          </div>

          <div className="menu-view-section">
            <span className="menu-view-label">Menu prices</span>
            {menu.menuPrices.length === 0 ? (
              <p className="menu-view-note">No menu prices. This menu shows library and location prices.</p>
            ) : (
              menu.menuPrices.map((row, i) => {
                const label = priceRowLabel(row);
                return (
                  <div className="menu-view-row" key={`${row.itemId}:${row.priceId || row.modifierId || i}`}>
                    <span className="menu-view-row-name">
                      {row.itemName}
                      {label && <span className="menu-view-row-detail"> · {label}</span>}
                    </span>
                    <span className="menu-view-row-value">{row.price}</span>
                  </div>
                );
              })
            )}
          </div>

          <p className="menu-view-note">To rename this menu, rename its design.</p>
        </>
      )}
      <footer>
        <Button size="large" onClick={onClose}>
          Close
        </Button>
      </footer>
    </Modal>
  );
}

/** What a menu shows for the item: "54 / 42", saying so when it's the menu's own price. */
function menuPriceSummary(menu: ItemMenuDto): string {
  const values = menu.prices.map((p) => (p.price || "").trim()).filter(Boolean).join(" / ");
  if (!values) return "No price";
  return menu.prices.some((p) => p.tier === "MENU") ? `menu price ${values}` : values;
}

interface UsedOnProps {
  itemId?: string;
  itemName: string;
  count: number;
  /** How the count reads where it sits: "3 menus", "Used on 3 menus", or "Used on 3". */
  variant?: "count" | "sentence" | "short";
  /** Shown when the count is 0. */
  empty?: ReactNode;
}

/**
 * "Used on N menus" anywhere it appears - the drawer, the Items table, search - opens the same list:
 * each menu with its location and the price it shows there, each one opening that menu (Oct 12).
 * The count is the same everywhere because every place reads the item's own `usedOnCount`.
 */
export function DataKitUsedOnLink({ itemId, itemName, count, variant = "count", empty }: UsedOnProps) {
  const [open, setOpen] = useState(false);
  const [menus, setMenus] = useState<ItemMenuDto[] | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const locationOf = useMenuLocation();

  if (count <= 0 || !itemId) return <>{empty ?? "––"}</>;

  const text =
    variant === "sentence"
      ? `Used on ${plural(count, "menu")}`
      : variant === "short"
        ? `Used on ${count}`
        : plural(count, "menu");

  const show = (e: MouseEvent) => {
    // Rows that host this link open the item on click; this opens the list instead.
    e.stopPropagation();
    setOpen(true);
    setMenus(null);
    DataKitApi.listItemMenus(itemId)
      .then((res) => setMenus(res.data || []))
      .catch(() => {
        message.error("Could not load the menus using this item");
        setOpen(false);
      });
  };

  return (
    <>
      <button type="button" className="data-kit-used-on-link" onClick={show}>
        {text}
      </button>
      {open && (
        <Modal
          className={"data-kit-used-on clean one"}
          open
          width={560}
          onCancel={() => setOpen(false)}
          // Rendered inside table rows: keep clicks in the modal from reaching the row.
          modalRender={(node) => <div onClick={(e) => e.stopPropagation()}>{node}</div>}
        >
          <header>
            <h2>{itemName}</h2>
          </header>
          {!menus ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : (
            <>
              <p className="menu-view-sub">Used on {plural(menus.length, "menu")}</p>
              {menus.length === 0 ? (
                <p className="menu-view-note">Not on any menu yet.</p>
              ) : (
                [...menus]
                  .sort(
                    (a, b) =>
                      (locationOf(a) || "").localeCompare(locationOf(b) || "") || a.name.localeCompare(b.name)
                  )
                  .map((menu) => (
                    <button
                      type="button"
                      className="menu-view-row menu-view-row-link"
                      key={menu.designId}
                      onClick={() => setViewing(menu.designId)}
                    >
                      <span className="menu-view-row-name">
                        {menu.name}
                        {locationOf(menu) && <span className="menu-view-row-detail"> · {locationOf(menu)}</span>}
                      </span>
                      <span className="menu-view-row-value">{menuPriceSummary(menu)}</span>
                    </button>
                  ))
              )}
            </>
          )}
          <footer>
            <Button size="large" onClick={() => setOpen(false)}>
              Close
            </Button>
          </footer>
        </Modal>
      )}
      {viewing && <DataKitMenuView designId={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
